import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import FileUpload from '../../../files/FileUpload';

import PDFDesignerTable from '../PDFDesignerTable';
import { isRichText } from '../richTextHelpers';

import { defaultDrawOptions } from '../PDFDesigner.constants';
import { rgba, imageValidator, isFieldLinked } from '../PDFDesignerHelpers';
import { hasSavedSectionLayout } from '../../pdfHelpers';

function PDFDesignerItem({
  text,
  image,
  rows,
  type,
  drawOptions = defaultDrawOptions,
  onMoveStart,
  onImageAdd,
  onMove,
  isDisplay,
  id,
  editing,
  onTableResize,
  link = {},
  sectionLayoutDrawOptions,
  sections = [],
}) {
  const {
    rowHeight,
    color,
    backgroundColor,
    fontFamily,
    fontSize,
    textAlign,
    verticalAlign,
    linkTextStyle = {},
    borderColor,
    borderStyle,
    borderWidth,
    headerOptions = {},
    width,
  } = drawOptions;

  const {
    backgroundColor: headerBackgroundColor,
    color: headerFontColor,
    borderColor: headerBorderColor,
  } = headerOptions;

  const isHeaderOrFooter = type === 'header' || type === 'footer';

  const ref = useRef();
  const [hovering, setHovering] = useState(false);
  const [mouseDown, setMouseDown] = useState(false);

  const onClick = useCallback(isDisplay || isHeaderOrFooter ? null : (e) => {
    const { clientX, clientY } = e;
    const { x: parentX, y: parentY } = ref.current.getBoundingClientRect();

    const offsetX = clientX - parentX;
    const offsetY = clientY - parentY;

    onMoveStart({ x: offsetX, y: offsetY });
    setMouseDown(true);
  }, [onMoveStart, isDisplay, isHeaderOrFooter]);

  const onMouseEnter = useCallback(isDisplay ? null : () => {
    setHovering(true);
  }, [isDisplay]);

  const onMouseLeave = useCallback(isDisplay ? null : () => {
    if (!mouseDown) {
      setHovering(false);
    }
  }, [mouseDown, isDisplay]);

  const onMouseUp = useCallback(isDisplay ? null : () => {
    setHovering(false);
    setMouseDown(false);
  }, [isDisplay, onMove, id]);

  const handleTableResize = useCallback(isDisplay ? null : onTableResize, [onTableResize]);

  const repeatableSectionPreview = useMemo(() => {
    if (type !== 'repeatableSection') return null;
    const hasCustomLayout = hasSavedSectionLayout(sectionLayoutDrawOptions);
    const linkedSection = sections.find((s) => s.id === link.sectionId);
    const sectionName = linkedSection?.name ?? 'Section';
    return { sectionName, hasCustomLayout };
  }, [type, sections, link.sectionId, sectionLayoutDrawOptions]);

  const textContent = useMemo(() => {
    if (type !== 'text') return null;

    const useLinkStyle = isFieldLinked(link);

    if (useLinkStyle) {
      const spanStyle = {
        fontWeight: linkTextStyle.bold ? 'bold' : 'normal',
        fontStyle: linkTextStyle.italic ? 'italic' : 'normal',
        textDecoration: linkTextStyle.underline ? 'underline' : 'none',
      };
      const normalizedText = Array.isArray(text) ? text.map((segment) => segment.content).join('') : text;
      return <span style={spanStyle}>{normalizedText || ''}</span>;
    }

    if (isRichText(text)) {
      return text.map((segment, index) => {
        const bold = useLinkStyle && linkTextStyle.bold !== undefined
          ? linkTextStyle.bold : segment.bold;
        const italic = useLinkStyle && linkTextStyle.italic !== undefined
          ? linkTextStyle.italic : segment.italic;
        const underline = useLinkStyle && linkTextStyle.underline !== undefined
          ? linkTextStyle.underline : segment.underline;
        return (
          <span
            key={index} // eslint-disable-line react/no-array-index-key
            style={{
              fontWeight: bold ? 'bold' : 'normal',
              fontStyle: italic ? 'italic' : 'normal',
              textDecoration: underline ? 'underline' : 'none',
            }}
          >
            {segment.content}
          </span>
        );
      });
    }

    return text || '';
  }, [type, text, link, linkTextStyle]);

  const className = useMemo(() => {
    if (isDisplay || isHeaderOrFooter) return 'pdf-designer-text-display';
    return hovering
      ? 'pdf-designer-text-edit-red' : 'pdf-designer-text-edit';
  }, [hovering, isDisplay, isHeaderOrFooter]);

  const itemStyle = {
    width: '100%',
    height: '100%',
  };

  if (isHeaderOrFooter) itemStyle.padding = 0;

  if (type !== 'table' && type !== 'repeatableSection') {
    itemStyle.justifyContent = verticalAlign;
    itemStyle.backgroundColor = rgba(backgroundColor);
  }

  const tableStyle = {
    fontFamily,
    fontSize,
    textAlign,
    color: rgba(color),
    justifyContent: verticalAlign,
    width,
    backgroundColor: rgba(backgroundColor),
  };

  const tableHeaderStyle = { ...headerOptions };
  if (headerFontColor) tableHeaderStyle.color = rgba(headerFontColor);
  if (headerBackgroundColor) tableHeaderStyle.backgroundColor = rgba(headerBackgroundColor);
  if (headerBorderColor) tableHeaderStyle.borderColor = rgba(headerBorderColor);

  if (borderStyle !== 'none' && type !== 'repeatableSection') {
    if (type === 'table') {
      tableStyle.borderColor = rgba(borderColor);
      tableStyle.borderStyle = borderStyle;
      tableStyle.borderWidth = borderWidth;
    } else {
      itemStyle.borderColor = rgba(borderColor);
      itemStyle.borderStyle = borderStyle;
      itemStyle.borderWidth = borderWidth;
    }
  }

  return (
    <div // eslint-disable-line jsx-a11y/no-static-element-interactions
      ref={ref}
      className={className}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onMouseUp={onMouseUp}
      onMouseDown={onClick}
      style={itemStyle}
    >
      {type === 'text' && (
        <div
          className="pdf-designer-text-container"
          style={{
            pointerEvents: editing ? 'auto' : 'none',
            zIndex: editing ? 2 : undefined,
            fontFamily,
            fontSize,
            textAlign,
            color: rgba(color),
            lineHeight: `${fontSize + 2}px`,
            paddingTop: 5,
            width: '100%',
            height: '100%',
            overflow: 'hidden',
          }}
        >
          {textContent}
        </div>
      )}
      {type === 'image' && !image && (
        <FileUpload
          style={{ top: 0 }}
          addFile={onImageAdd}
          customValidator={imageValidator}
        />
      )}
      {type === 'image' && image && (
        <img
          className="pdf-designer-image"
          src={image instanceof File ? URL.createObjectURL(image) : image}
          draggable={false}
          alt="Ontraccr PDF Designer"
        />
      )}
      {type === 'table' && (
        <PDFDesignerTable
          rows={rows}
          style={tableStyle}
          rowHeight={rowHeight}
          headerOptions={tableHeaderStyle}
          onResize={handleTableResize}
        />
      )}
      {isHeaderOrFooter && !isDisplay && (
        <div
          className="pdf-designer-text-container"
          style={{
            backgroundColor: '#FFCCCB',
            padding: 0,
          }}
        />
      )}
      {type === 'repeatableSection' && repeatableSectionPreview && (
        <div
          className="pdf-designer-repeatable-section"
        >
          <div
            className="pdf-designer-repeatable-section-title"
            style={{ fontFamily: 'Helvetica' }}
          >
            {'Repeatable - '}
            {repeatableSectionPreview.sectionName}
            {repeatableSectionPreview.hasCustomLayout && (
              <span style={{ marginLeft: 8, opacity: 0.85 }}>
                (Custom layout)
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

PDFDesignerItem.propTypes = {
  text: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.shape({}))]),
  image: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(File)]),
  rows: PropTypes.arrayOf(PropTypes.shape({})),
  type: PropTypes.string,
  drawOptions: PropTypes.shape({}),
  onMoveStart: PropTypes.func,
  onImageAdd: PropTypes.func,
  onMove: PropTypes.func,
  isDisplay: PropTypes.bool,
  id: PropTypes.string,
  editing: PropTypes.bool,
  onTableResize: PropTypes.func,
  link: PropTypes.shape({
    sectionId: PropTypes.string,
    fieldId: PropTypes.string,
    collectedId: PropTypes.string,
  }),
  sectionLayoutDrawOptions: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.shape({}))),
  sections: PropTypes.arrayOf(PropTypes.shape({})),
};

PDFDesignerItem.defaultProps = {
  text: undefined,
  image: undefined,
  rows: undefined,
  type: undefined,
  drawOptions: undefined,
  onMoveStart: undefined,
  onImageAdd: undefined,
  onMove: undefined,
  isDisplay: false,
  id: undefined,
  editing: false,
  onTableResize: undefined,
  link: {},
  sectionLayoutDrawOptions: undefined,
  sections: [],
};

export default PDFDesignerItem;
